const STORAGE_KEY = 'ct_consent_v1';
export const CONSENT_UPDATED_EVENT = 'ct:consent:updated';
export const CONSENT_VERSION = '1.0.0';

export type ConsentCategory = 'analytics' | 'marketing';
export interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentRecord {
  version: string;
  timestamp: string;
  categories: ConsentPreferences & { essential: true };
}

type ConsentEventDetail = {
  consent: ConsentRecord | null;
};

const isBrowser = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

type ConsentChangeCallback = (consent: ConsentRecord | null) => void;

const listeners = new Set<ConsentChangeCallback>();

const notifyListeners = (consent: ConsentRecord | null) => {
  listeners.forEach((listener) => {
    try {
      listener(consent);
    } catch (error) {
      console.error('Consent listener failed.', error);
    }
  });
};

const emitConsentEvent = (consent: ConsentRecord | null) => {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<ConsentEventDetail>(CONSENT_UPDATED_EVENT, {
      detail: { consent },
    }),
  );
};

export const getConsent = (): ConsentRecord | null => {
  if (!isBrowser()) {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);

    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as ConsentRecord;

    if (!parsed || parsed.version !== CONSENT_VERSION) {
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn('Unable to read consent preferences from storage.', error);
    return null;
  }
};

export const onConsentChange = (callback: ConsentChangeCallback) => {
  listeners.add(callback);

  if (isBrowser()) {
    callback(getConsent());
  }

  return () => {
    listeners.delete(callback);
  };
};

export const setConsent = (preferences: ConsentPreferences): ConsentRecord => {
  const consent: ConsentRecord = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories: {
      essential: true,
      analytics: preferences.analytics,
      marketing: preferences.marketing,
    },
  };

  if (!isBrowser()) {
    return consent;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch (error) {
    console.warn('Unable to persist consent preferences to storage.', error);
  }

  emitConsentEvent(consent);
  notifyListeners(consent);

  return consent;
};

export const hasConsent = (category: ConsentCategory): boolean => {
  const consent = getConsent();

  if (!consent) {
    return false;
  }

  return Boolean(consent.categories[category]);
};

export const revokeConsent = () => {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Unable to clear consent preferences from storage.', error);
  }

  emitConsentEvent(null);
  notifyListeners(null);
};
